import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RoleProvider } from "./contexts/RoleContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { NotificationContainer } from "./components/ui/Notification";
import { useNotification } from "./contexts/NotificationContext";
import "react-toastify/dist/ReactToastify.css";

import MainLayout from "./components/layout/MainLayout";

// Pages
import Login from "./pages/Login";

import Dashboard from "./pages/Dashboard";
import AnimatedDashboard from "./pages/AnimatedDashboard";
import UserManagement from "./pages/UserManagement/UserManagement";
import RoleManagement from "./pages/RoleManagement/RoleManagement";
import SubscriptionManagement from "./pages/SubscriptionManagement/SubscriptionManagement";
import AdsManagement from "./pages/AdsManagement/AdsManagement";
import CMSPage from "./pages/cms/PageList";
import LoadingTransition from "./components/ui/LoadingTransition";
import OtpConfirmation from "./pages/OtpConfirmation";
import ResetPassword from "./pages/resetpassword";
import Skeleton from "./components/ui/Skeleton";
import { Flip, ToastContainer } from "react-toastify";

import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ContactSupport from "./pages/ContactSupport";
import DeleteAccount from "./pages/DeleteAccount";
import Sound from "./pages/Sound/Sound";
import Pet from "./pages/Pet/Pet";
import BackgroundPattern from "./pages/BackgroundPattern/BackgroundPattern";
import ContentList from "./pages/cms/content/ContentList";
import ContactList from "./pages/Contact/ContactList";
import ChangePassword from "./pages/ChangePassword";
import Profile from "./pages/Profile";
import AccessoriesList from "./pages/Accessories/AccessoriesList";
import SubscribedUsers from "./pages/SubscribedUsers/SubscribedUsers";
import Trigger from "./pages/Trigger/Trigger";
import PetPackageManagement from "./pages/PetPackage/PetPackageManagement";
import NotificationManagement from "./pages/NotificationManagement/NotificationManagement";
import PaymentManagement from "./pages/PaymentManagemt/PaymentManagement";

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (!isAuthenticated && !isLoading) return <Navigate to="/login" />;
  return (
    <LoadingTransition
      isLoading={isLoading}
      skeleton={<Skeleton.Card header={true} rows={6} />}
    >
      {children}
    </LoadingTransition>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/verify-otp" element={<OtpConfirmation />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/contact" element={<ContactSupport />} />
      <Route path="/delete-account" element={<DeleteAccount />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AnimatedDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard-static"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-users"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RoleManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SubscriptionManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ads"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AdsManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/cms/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CMSPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sound"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Sound />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pets"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Pet />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bg-patterns"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BackgroundPattern />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accessories"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AccessoriesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/pet-packages"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PetPackageManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/cms/contents/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ContentList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact-us-management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ContactList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PaymentManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile/change-password"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ChangePassword />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Profile />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribed-user"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SubscribedUsers />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trigger"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Trigger />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all route - redirects to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Notifications wrapper component
function NotificationsWrapper() {
  const { notifications, removeNotification } = useNotification();

  return (
    <NotificationContainer
      notifications={notifications}
      onClose={removeNotification}
    />
  );
}

// Main App component
function App() {
  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
        transition={Flip}
      />
      <Router>
        <AuthProvider>
          <RoleProvider>
            <NotificationProvider>
              <div className="app">
                <AppRouter />
                <NotificationsWrapper />
              </div>
            </NotificationProvider>
          </RoleProvider>
        </AuthProvider>
      </Router>
    </>
  );
}

export default App;
